import React, { useEffect, useState } from 'react';
import { ActivityIndicator, Modal, View } from 'react-native';
import { createStyleSheet, useStyles } from 'react-native-unistyles';
import MyAppText from '../../../components/MyAppText';
import { translate } from '../../../services/translate';
import { useAuth } from '../../../context/Auth';
import ActingBodiesDropdown from './ActingBodiesDropdown';
import FilterDatePicker from '../../../components/Filter/components/FilterDateInput';
import TypeDropdown from './TypeDropdown';
import { dispatchService } from '../../../services/central-api/dispatch';
import MyRadio from '../../../components/MyRadio';
import { WindowInformation } from '../../../services/window-information';
import { ScrollView } from 'react-native-web-hover';
import CustomButton from '../../../components/CustomButton';
import DeleteModal from '../../../components/DeleteModal';
import moment from 'moment';
import Toast from 'react-native-toast-message';

interface ModalProps {
    isModalVisible: boolean;
    setModalVisible: (value: boolean) => void;
    onSubmit: () => void;
    onCancel?: () => void;
}

export default function BulkFinishModal({
    isModalVisible,
    setModalVisible,
    onSubmit,
    onCancel,
}: ModalProps) {
    const { styles, theme } = useStyles(styleSheet);
    const { user: authenticatedUser } = useAuth();
    const windowInfo = WindowInformation();

    const [bulkFinishLoading, setBulkFinishLoading] = useState(false);
    const [selectedActingBodyId, setSelectedActingBodyId] = useState<number>(authenticatedUser?.actingBodyUnit.actingBodyId ?? 0);
    const [beginInDateFilter, setBeginInDateFilter] = useState<Date | null>(null);
    const [beginInHourFilter, setBeginInHourFilter] = useState('');
    const [endInDateFilter, setEndInDateFilter] = useState<Date | null>(null);
    const [endInHourFilter, setEndInHourFilter] = useState('');
    const [selectedTriggerTypeFilter, setSelectedTriggerTypeFilter] = useState<(TriggerType | 'manual')[]>([]);

    const [finishCategory, setFinishCategory] = useState<number>(0);
    const [finishSubCategory, setFinishSubCategory] = useState<number>(0);
    const [finishCategories, setFinishCategories] = useState<FinishCategoryBase[]>([]);
    const [loading, setIsLoading] = useState(false);

    const [confirmationModalVisible, setConfirmationModalVisible] = useState(false);

    async function loadInfo() {
        try {
            if (selectedActingBodyId !== 0) {
                setIsLoading(true);
                setFinishCategory(0);
                setFinishSubCategory(0);

                const finishCategories = await dispatchService.getSimplifiedFinishReasons(selectedActingBodyId);
                setFinishCategories(finishCategories);
            }
        } finally {
            setIsLoading(false);
        }

    }

    function closeModal() {
        onSubmit();
        setModalVisible(false);
        setFinishCategory(0);
        setFinishSubCategory(0);
        setSelectedActingBodyId(0);
        setBeginInDateFilter(null);
        setBeginInHourFilter('');
        setEndInDateFilter(null);
        setEndInHourFilter('');
        setSelectedTriggerTypeFilter([]);
        setFinishCategories([]);
    }

    useEffect(() => {
        if (!showActingBodyDropdown()) {
            setSelectedActingBodyId(authenticatedUser?.actingBodyUnit.actingBodyId ?? 0);
        }

        if (isModalVisible) {
            loadInfo();
        }
    }, [isModalVisible, showActingBodyDropdown() && selectedActingBodyId]);

    function getDates() {
        const formatDateTime = (date: Date | null, time: string) =>
            date ? moment(`${moment(date).format('DD/MM/YYYY')} ${time}`, 'DD/MM/YYYY HH:mm:ss').valueOf() : undefined;
        const beginIn = formatDateTime(beginInDateFilter, beginInHourFilter ? `${beginInHourFilter}:00` : '00:00:00');
        const endIn = formatDateTime(endInDateFilter, endInHourFilter ? `${endInHourFilter}:59` : '23:59:59');

        return { beginIn, endIn };
    }

    function allowBulkFinish(): boolean {
        const { beginIn, endIn } = getDates();

        return selectedActingBodyId !== 0
            && beginIn !== undefined
            && endIn !== undefined
            && beginIn <= endIn
            && finishCategory !== 0
            && finishSubCategory !== 0
            && selectedTriggerTypeFilter.length > 0;
    }

    function showActingBodyDropdown() {
        return authenticatedUser?.permissions.dispatch_manage_all_acting_bodies_occurrences_bulk_finish;
    }

    async function bulkFinishOccurrences() {
        try {
            setBulkFinishLoading(true);
            const { beginIn, endIn } = getDates();

            const response = await dispatchService.bulkFinishOccurrences({
                actingBodyId: selectedActingBodyId,
                beginIn,
                endIn,
                triggerType: selectedTriggerTypeFilter[0],
                finishCategoryId: finishCategory || null,
                finishSubCategoryId: finishSubCategory || null,
            });

            Toast.show({
                type: 'sentinelxSuccess',
                text1: translate('occurrencesBulkFinishSuccessMessage', {
                    count: response.count
                }),
            });
        } catch (err) {
            console.error(err);
            Toast.show({
                type: 'sentinelxError',
                text1: translate('unexpectedError'),
            });
            return;
        } finally {
            setBulkFinishLoading(false);
            closeModal();
        }
    }

    return (
        <Modal
            visible={isModalVisible}
            animationType='fade'
            onRequestClose={() => {
                onCancel && onCancel();
                setModalVisible(false);
            }}
            transparent={true}
        >
            <View style={styles.container}>
                <View style={styles.formContainer(windowInfo.isMobile)}>
                    <MyAppText style={styles.header}>{translate('occurrencesBulkFinish')}</MyAppText>
                    <ScrollView contentContainerStyle={{ flexDirection: 'column', rowGap: 16 }}>
                        {showActingBodyDropdown() &&
                            <ActingBodiesDropdown includeAllOption={false} value={selectedActingBodyId} setValue={setSelectedActingBodyId} zIndex={17} />
                        }
                        <FilterDatePicker
                            date={beginInDateFilter}
                            onChangeDate={(value) => {
                                setBeginInDateFilter(value);
                                if (value && !beginInHourFilter) {
                                    setBeginInHourFilter('00:00');
                                }
                            }}
                            time={beginInHourFilter}
                            onChangeTime={setBeginInHourFilter}
                            label={translate('createdAt')}
                            zIndex={16}
                        />

                        <FilterDatePicker
                            date={endInDateFilter}
                            onChangeDate={(value) => {
                                setEndInDateFilter(value);
                                if (value && !endInHourFilter) {
                                    setEndInHourFilter('23:59');
                                }
                            }}
                            time={endInHourFilter}
                            onChangeTime={setEndInHourFilter}
                            label={translate('to')}
                            zIndex={15}
                        />

                        <TypeDropdown single={true} removeManual={true} value={selectedTriggerTypeFilter} setValue={setSelectedTriggerTypeFilter} zIndex={13} />
                        {loading ?
                            <View style={{ height: 40 }}>
                                <ActivityIndicator size='small' color={theme.colors.loadingColor} />
                            </View>
                            : finishCategories.length > 0 &&
                            <View>
                                <MyAppText style={{ fontWeight: 'bold', fontSize: 16 }}>{translate('finishReason')}</MyAppText>
                                <View style={styles.content(windowInfo.isMobile)}>
                                    <MyRadio
                                        style={styles.customRadio}
                                        selected={finishCategory}
                                        setSelected={(value) => {
                                            setFinishCategory(value);
                                            setFinishSubCategory(0);
                                        }}
                                        options={finishCategories.map((finishCategory) => {
                                            return {
                                                label: finishCategory.name,
                                                value: finishCategory.id
                                            };
                                        })}
                                    />
                                    {finishCategory && (finishCategories.map(fg => fg.id)).includes(finishCategory) ?
                                        <View style={{ rowGap: 10, flex: 1 }}>
                                            <MyRadio
                                                style={styles.customRadio}
                                                selected={finishSubCategory}
                                                setSelected={(value) => {
                                                    setFinishSubCategory(value);
                                                }}
                                                options={(finishCategories.find(finishCat => finishCat.id == finishCategory) as FinishCategory).finishSubCategories
                                                    .map((finishSubCategory) => {
                                                        return {
                                                            label: finishSubCategory.name,
                                                            value: finishSubCategory.id
                                                        };
                                                    })}
                                            />
                                        </View> : null}
                                </View>
                            </View>
                        }

                    </ScrollView>
                    <View style={{ flexDirection: 'row', justifyContent: 'flex-end', columnGap: 16 }}>
                        <CustomButton onPress={closeModal} text={translate('cancel')} type='secondary' />
                        <CustomButton onPress={() => setConfirmationModalVisible(true)} text={translate('confirm')} disabled={!allowBulkFinish() || bulkFinishLoading} isLoading={bulkFinishLoading} type='destructive' />
                    </View>
                </View>
            </View>
            <DeleteModal
                isModalVisible={confirmationModalVisible}
                setModalVisible={setConfirmationModalVisible}
                customMessage={translate('thisActionCannotBeUndone')}
                onSubmit={bulkFinishOccurrences}
                customConfirm={translate('confirm')}
            />
        </Modal>
    );
}

const styleSheet = createStyleSheet((theme) => ({
    container: {
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        backgroundColor: '#31313199',
        flex: 1,
        paddingHorizontal: 16,
    },
    formContainer: (isMobile: boolean) => ({
        width: isMobile ? '95%' : '60%',
        height: '100%',
        backgroundColor: theme.colors.container.background,
        borderRadius: 4,
        padding: 20,
        rowGap: 32,
        maxHeight: '95%',
        overflow: 'hidden',
    }),
    header: {
        fontSize: 18,
        fontWeight: '700',
        flexWrap: 'wrap',
        color: theme.colors.modal.header,
    },
    content: (isMobile: boolean) => ({
        flexDirection: isMobile ? 'column' : 'row',
        gap: 30,
        flex: 1
    }),
    customRadio: {
        flexDirection: 'column',
        rowGap: 10,
    },
}));
